import { useEffect, useRef, useState } from "react";
import {
  fetchWelcomeMediaList, uploadWelcomeMedia, updateWelcomeMedia, uploadWelcomePoster, resetWelcomePoster,
  reprocessWelcomeMedia, deleteWelcomeMedia, formatApiErrorDetail,
} from "@/villa142/api";
import { ConfirmModal } from "@/villa142/admin/ConfirmModal";

const fmtBytes = (n) => (n == null ? "" : n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);
const fmtSec = (s) => (s == null ? "" : `${Number(s).toFixed(1)}s`);

const Preview = ({ item, mobile }) => {
  const u = item.urls || {};
  const poster = mobile ? u.poster_mobile || u.mobile_webp : u.poster_desktop || u.desktop_webp;
  const video = item.kind === "video" && item.status === "ready" ? (mobile ? u.mp4_mobile : u.mp4_desktop) : null;
  return (
    <div className={`v142-wm-frame ${mobile ? "is-mobile" : "is-desktop"}`} data-testid={`v142-wm-preview-${mobile ? "mobile" : "desktop"}-${item.id}`}>
      {poster ? <img src={poster} alt="" className="v142-wm-media" /> : <div className="v142-wm-empty">{item.status === "processing" ? "Processing…" : "No preview"}</div>}
      {video && <video className="v142-wm-media v142-wm-video" src={video} muted loop playsInline autoPlay preload="metadata" aria-hidden="true" />}
      <div className="v142-wm-scrim" aria-hidden="true" />
      <div className="v142-wm-stub" aria-hidden="true"><span /><span /><span className="is-short" /></div>
      <span className="v142-wm-frame-label">{mobile ? "Mobile" : "Desktop"}</span>
    </div>
  );
};

const LoopFields = ({ item, onApply }) => {
  const [start, setStart] = useState(item.loop_start ?? 0);
  const [dur, setDur] = useState(item.loop_duration ?? 12);
  return (
    <div className="v142-wm-loop" data-testid={`v142-wm-loop-${item.id}`}>
      <span className="v142-mini-label">Loop segment{item.source_duration ? ` · source ${fmtSec(item.source_duration)}` : ""}</span>
      <div className="v142-wm-loop-row">
        <label className="v142-mini-field">
          <span className="v142-mini-label">Start (s)</span>
          <input type="number" min="0" step="0.5" className="v142-input v142-input-small" value={start} onChange={(e) => setStart(e.target.value)} data-testid={`v142-wm-loop-start-${item.id}`} />
        </label>
        <label className="v142-mini-field">
          <span className="v142-mini-label">Length (s)</span>
          <input type="number" min="1" max="60" step="0.5" className="v142-input v142-input-small" value={dur} onChange={(e) => setDur(e.target.value)} data-testid={`v142-wm-loop-dur-${item.id}`} />
        </label>
        <button type="button" className="v142-btn v142-btn-small" data-testid={`v142-wm-loop-apply-${item.id}`} onClick={() => onApply(Number(start), Number(dur))}>
          Re-cut loop
        </button>
      </div>
    </div>
  );
};

const MediaCard = ({ item, index, count, onPatch, onMove, onArchive, onDelete, onPoster, onResetPoster, onReprocess }) => {
  const posterRef = useRef(null);
  const timer = useRef(null);
  const [title, setTitle] = useState(item.title);
  useEffect(() => { setTitle(item.title); }, [item.title]);

  const saveTitle = (v) => {
    setTitle(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onPatch({ title: v }), 700);
  };

  return (
    <article className={`v142-wm-card${item.active ? " is-active" : ""}${item.archived ? " is-archived" : ""}`} data-testid={`v142-wm-card-${item.id}`}>
      <header className="v142-wm-head">
        <span className="v142-wm-badge" data-testid={`v142-wm-kind-${item.id}`}>{item.kind}</span>
        <span className={`v142-wm-status is-${item.status}`} data-testid={`v142-wm-status-${item.id}`}>
          {item.status === "processing" ? `Processing…${item.phase ? ` ${item.phase.toLowerCase()}` : ""}` : item.status === "failed" ? "Failed" : item.active ? "Active — shown to clients" : "Ready"}
        </span>
      </header>
      <div className="v142-wm-previews">
        <Preview item={item} mobile={false} />
        <Preview item={item} mobile />
      </div>
      <label className="v142-mini-field">
        <span className="v142-mini-label">Title (admin reference only)</span>
        <input className="v142-input v142-input-small" value={title} onChange={(e) => saveTitle(e.target.value)} data-testid={`v142-wm-title-${item.id}`} />
      </label>
      <p className="v142-entry-meta" data-testid={`v142-wm-meta-${item.id}`}>
        {item.filename}{item.size ? ` · ${fmtBytes(item.size)} source` : ""}
        {item.width ? ` · ${item.width}×${item.height}` : ""}
        {item.kind === "video" && item.sizes?.mp4_desktop ? ` · MP4 ${fmtBytes(item.sizes.mp4_desktop)} / mobile ${fmtBytes(item.sizes.mp4_mobile)}` : ""}
        {item.kind === "video" && item.sizes?.webm_desktop ? ` · WebM ${fmtBytes(item.sizes.webm_desktop)}` : ""}
        {item.kind === "image" && item.sizes?.desktop_avif ? ` · AVIF ${fmtBytes(item.sizes.desktop_avif)} / mobile ${fmtBytes(item.sizes.mobile_avif)}` : ""}
      </p>
      {item.error && <p className="v142-error" role="alert" data-testid={`v142-wm-error-${item.id}`}>{item.error}</p>}
      {item.kind === "video" && !item.archived && <LoopFields key={`${item.loop_start}-${item.loop_duration}`} item={item} onApply={(s, d) => onPatch({ loop_start: s, loop_duration: d })} />}
      {!item.archived && (
        <div className="v142-wm-actions">
          {!item.active && item.status === "ready" && (
            <button type="button" className="v142-btn v142-btn-small" data-testid={`v142-wm-activate-${item.id}`} onClick={() => onPatch({ active: true })}>Make active</button>
          )}
          {item.active && (
            <button type="button" className="v142-link" data-testid={`v142-wm-deactivate-${item.id}`} onClick={() => onPatch({ active: false })}>Set inactive</button>
          )}
          <button type="button" className="v142-link" aria-label="Move up" disabled={index === 0} onClick={() => onMove(-1)} data-testid={`v142-wm-up-${item.id}`}>↑</button>
          <button type="button" className="v142-link" aria-label="Move down" disabled={index >= count - 1} onClick={() => onMove(1)} data-testid={`v142-wm-down-${item.id}`}>↓</button>
          {item.kind === "video" && (
            <>
              <button type="button" className="v142-link" data-testid={`v142-wm-poster-${item.id}`} onClick={() => posterRef.current?.click()}>Replace poster</button>
              {item.poster_custom && <button type="button" className="v142-link" data-testid={`v142-wm-poster-reset-${item.id}`} onClick={onResetPoster}>Use frame from video</button>}
              <input ref={posterRef} type="file" accept="image/*" className="v142-visually-hidden" aria-label="Poster image" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onPoster(f); }} />
            </>
          )}
          {(item.status === "failed" || (item.error && item.status !== "processing")) && <button type="button" className="v142-link" onClick={onReprocess} data-testid={`v142-wm-retry-${item.id}`}>Try again</button>}
          <button type="button" className="v142-link v142-danger-link" data-testid={`v142-wm-archive-${item.id}`} onClick={onArchive}>Archive</button>
        </div>
      )}
      {item.archived && (
        <div className="v142-wm-actions">
          <button type="button" className="v142-link" data-testid={`v142-wm-restore-${item.id}`} onClick={() => onPatch({ archived: false })}>Restore</button>
          <button type="button" className="v142-link v142-danger-link" data-testid={`v142-wm-delete-${item.id}`} onClick={onDelete}>Delete permanently</button>
        </div>
      )}
    </article>
  );
};

export default function WelcomeMediaView() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [surface, setSurface] = useState("welcome");
  const [kind, setKind] = useState("image");
  const [title, setTitle] = useState("");
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try { setItems(await fetchWelcomeMediaList()); } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const processing = (items || []).some((i) => i.status === "processing");
  useEffect(() => {
    if (!processing) return undefined;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [processing]);

  const run = async (fn) => {
    setError("");
    try { await fn(); await load(); } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onFile = async (file) => {
    if (!file) return;
    setProgress(0);
    await run(() => uploadWelcomeMedia(file, kind, title, setProgress, false, surface));
    setProgress(null);
    setTitle("");
    if (fileRef.current) fileRef.current.value = "";
  };

  if (!items) {
    return error ? (
      <div data-testid="v142-welcome-media-view">
        <p className="v142-error" role="alert" data-testid="v142-wm-error">{error}</p>
        <button type="button" className="v142-link" data-testid="v142-wm-reload" onClick={() => { setError(""); load(); }}>Try loading again</button>
      </div>
    ) : <p className="v142-copy">Loading…</p>;
  }
  const mine = items.filter((i) => (i.surface || "welcome") === surface);
  const live = mine.filter((i) => !i.archived);
  const archived = mine.filter((i) => i.archived);

  const move = (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= live.length) return;
    run(async () => {
      await updateWelcomeMedia(live[idx].id, { order: t });
      await updateWelcomeMedia(live[t].id, { order: idx });
    });
  };

  return (
    <div data-testid="v142-welcome-media-view">
      <div className="v142-wm-surfaces" role="tablist" aria-label="Media surface" data-testid="v142-wm-surfaces">
        {[["welcome", "Welcome media"], ["ending", "Ending media"]].map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={surface === id}
            className={`v142-wm-surface${surface === id ? " is-active" : ""}`}
            data-testid={`v142-wm-surface-${id}`}
            onClick={() => setSurface(id)}
          >
            {label}
            <span className="v142-wm-surface-count">{items.filter((i) => (i.surface || "welcome") === id && i.active && !i.archived).length ? "● active" : "○ none active"}</span>
          </button>
        ))}
      </div>
      <p className="v142-copy" data-testid="v142-wm-intro">
        {surface === "welcome" ? (
          <>The full-bleed media behind <em>“A little space to reflect.”</em>, shown after the password.</>
        ) : (
          <>The full-bleed media behind the closing <em>“Thank you”</em> screen, shown once the clients share their reflections.</>
        )}{" "}
        Keep up to three candidates per surface to compare; only the one marked <strong>Active</strong> is shown to clients,
        and each surface has its own active choice. Videos are cut to a short muted loop, re-encoded for desktop and
        mobile, and given a poster frame that paints first. Images become responsive AVIF/WebP.
      </p>
      {error && <p className="v142-error" role="alert" data-testid="v142-wm-error">{error}</p>}

      {live.length < 3 && (
        <div className="v142-wm-upload" data-testid="v142-wm-upload">
          <div className="v142-wm-upload-row">
            <fieldset className="v142-wm-kind" data-testid="v142-wm-kind-select">
              <legend className="v142-mini-label">Media type</legend>
              {["image", "video"].map((k) => (
                <label key={k} className={`v142-wm-kind-opt${kind === k ? " is-on" : ""}`}>
                  <input type="radio" name="v142-wm-kind" value={k} checked={kind === k} onChange={() => setKind(k)} data-testid={`v142-wm-kind-${k}`} />
                  {k === "image" ? "Image" : "Video"}
                </label>
              ))}
            </fieldset>
            <label className="v142-mini-field v142-wm-title-field">
              <span className="v142-mini-label">Title (optional)</span>
              <input className="v142-input v142-input-small" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Evening water, take 2" data-testid="v142-wm-new-title" />
            </label>
            <input
              ref={fileRef}
              type="file"
              accept={kind === "video" ? "video/*,.mp4,.mov,.webm,.mkv,.m4v" : "image/*,.heic"}
              className="v142-visually-hidden"
              id="v142-wm-file"
              data-testid="v142-wm-file"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <button type="button" className="v142-btn v142-btn-small" disabled={progress != null} data-testid="v142-wm-upload-btn" onClick={() => fileRef.current?.click()}>
              {progress != null ? `Uploading ${Math.round(progress * 100)}%` : `Upload ${kind}`}
            </button>
          </div>
          {progress != null && <div className="v142-wm-progress" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${progress * 100}%` }} /></div>}
        </div>
      )}

      <div className="v142-wm-grid" data-testid="v142-wm-grid">
        {live.map((it, i) => (
          <MediaCard
            key={it.id}
            item={it}
            index={i}
            count={live.length}
            onPatch={(p) => run(() => updateWelcomeMedia(it.id, p))}
            onMove={(d) => move(i, d)}
            onArchive={() => setConfirm({ title: "Archive this media", body: `“${it.title}” will be hidden from the candidates${it.active ? " and clients will see the quiet ivory interstitial until another asset is made active" : ""}. You can restore it later.`, note: null, label: "Archive", action: () => updateWelcomeMedia(it.id, { archived: true }) })}
            onDelete={() => {}}
            onPoster={(f) => run(() => uploadWelcomePoster(it.id, f))}
            onResetPoster={() => run(() => resetWelcomePoster(it.id))}
            onReprocess={() => run(() => reprocessWelcomeMedia(it.id))}
          />
        ))}
        {live.length === 0 && <p className="v142-copy" data-testid="v142-wm-none">No {surface} media yet — clients see the quiet ivory version of this screen.</p>}
      </div>

      {archived.length > 0 && (
        <details className="v142-wm-archived" data-testid="v142-wm-archived">
          <summary>Archived ({archived.length})</summary>
          <div className="v142-wm-grid">
            {archived.map((it) => (
              <MediaCard
                key={it.id}
                item={it}
                index={0}
                count={1}
                onPatch={(p) => run(() => updateWelcomeMedia(it.id, p))}
                onMove={() => {}}
                onArchive={() => {}}
                onDelete={() => setConfirm({ title: "Delete permanently", body: `Delete “${it.title}” and its processed files? The original upload cannot be recovered afterwards.`, label: "Delete", action: () => deleteWelcomeMedia(it.id) })}
                onPoster={() => {}}
                onResetPoster={() => {}}
                onReprocess={() => {}}
              />
            ))}
          </div>
        </details>
      )}

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.title}
        body={confirm?.body}
        note={confirm?.note === null ? null : undefined}
        confirmLabel={confirm?.label}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => { setBusy(true); try { await run(confirm.action); setConfirm(null); } finally { setBusy(false); } }}
      />
    </div>
  );
}
